import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tooltip, Modal } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
} from '@ant-design/icons';
import LibraryPage from '../../../components/layout/LibraryPage';
import { TGDataTable, StatusTag } from '../../../components/common';
import TemplateWizard from './TemplateWizard';
import LastModifiedFilterBar from './LastModifiedFilterBar';
import {
  useListTemplatesQuery,
  useDeleteTemplateMutation,
  useCreateTemplateMutation,
} from '../../../store/api/templateApi';
import { formatDate } from '../../../utils/dateUtils';
import { toast } from '../../../components/common/Toast';

export default function TemplateList({ embedded = false }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: 'all', type: 'all' });
  const [wizardOpen, setWizardOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  // RTK Query hooks
  const { data: response, isLoading, refetch } = useListTemplatesQuery(filters);
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [createTemplate] = useCreateTemplateMutation();

  const templates = response?.data || [];

  // Filter templates by last modified date
  const filteredTemplates = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return templates;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return templates.filter((template) => {
      if (!template.updated_at) return false;
      const templateTime = new Date(template.updated_at).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return templateTime === filterTime;
        case 'after':
          return templateTime > filterTime;
        case 'before':
          return templateTime < filterTime;
        default:
          return true;
      }
    });
  }, [templates, dateFilter]);

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sortable: true,
        filterable: true,
        filterType: 'text',
        width: 300,
        render: (name, record) => (
          <Space>
            <FileTextOutlined style={{ color: '#8B5CF6' }} />
            <a
              onClick={() => navigate(`/library/builder/${record.id}`)}
              style={{ color: '#8B5CF6', fontWeight: 500 }}
            >
              {name}
            </a>
          </Space>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'select',
        filterOptions: [
          { label: 'RFP', value: 'rfp' },
          { label: 'RFI', value: 'rfi' },
          { label: 'RFQ', value: 'rfq' },
          { label: 'Survey', value: 'survey' },
          { label: 'Assessment', value: 'assessment' },
        ],
        render: (type) => (
          <StatusTag
            status={type}
            color={
              type === 'rfp'
                ? 'purple'
                : type === 'rfi'
                ? 'blue'
                : type === 'rfq'
                ? 'cyan'
                : 'green'
            }
          >
            {type.toUpperCase()}
          </StatusTag>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'select',
        filterOptions: [
          { label: 'Draft', value: 'draft' },
          { label: 'Published', value: 'published' },
          { label: 'Archived', value: 'archived' },
        ],
        render: (status) => {
          const statusConfig = {
            draft: { color: 'default', label: 'Draft' },
            published: { color: 'success', label: 'Published' },
            archived: { color: 'warning', label: 'Archived' },
          };
          const config = statusConfig[status] || statusConfig.draft;
          return <StatusTag status={status} color={config.color}>{config.label}</StatusTag>;
        },
      },
      {
        title: 'Version',
        dataIndex: 'version',
        key: 'version',
        width: 100,
        sortable: true,
        filterable: true,
        filterType: 'number',
        render: (version) => `v${version || 1}`,
      },
      {
        title: 'Created By',
        dataIndex: 'created_by_name',
        key: 'created_by',
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        title: 'Last Modified',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180,
        sortable: true,
        render: (date) => formatDate(date),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            {record.status === 'published' && (
              <Tooltip title="Complete">
                <Button
                  type="primary"
                  size="small"
                  icon={<FormOutlined />}
                  onClick={() => navigate(`/library/complete/${record.id}`)}
                  style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
                />
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/library/builder/${record.id}`)}
              />
            </Tooltip>
            <Tooltip title="Preview">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/library/preview/${record.id}`)}
              />
            </Tooltip>
            <Tooltip title="Duplicate">
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleDuplicate(record)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Delete Template',
                    icon: <ExclamationCircleOutlined />,
                    content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
                    okText: 'Delete',
                    okType: 'danger',
                    onOk: () => handleDelete(record.id),
                  });
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [navigate]
  );

  const handleCreateNew = () => {
    setWizardOpen(true);
  };

  const handleDuplicate = async (template) => {
    try {
      const newTemplate = await createTemplate({
        name: `${template.name} (Copy)`,
        type: template.type,
        template_data: template.template_data,
      }).unwrap();

      toast.success('Template duplicated', `${newTemplate.name}`);
      refetch();
    } catch (error) {
      toast.error('Failed to duplicate template');
      console.error('Duplicate error:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTemplate(id).unwrap();
      toast.success('Template deleted');
      refetch();
    } catch (error) {
      toast.error('Failed to delete template');
      console.error('Delete error:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setDateFilter(null);
  };

  // If embedded in Library page, don't show LibraryPage wrapper
  const content = (
    <>
      <TGDataTable
        columns={columns}
        data={filteredTemplates}
        loading={isLoading}
        showExport
        showColumnConfig
        exportFilename="templates"
        presetKey="library.templates"
        onFilterChange={handleFilterChange}
        externalFilterCount={dateFilter && dateFilter.date ? 1 : 0}
        onClearExternalFilters={handleClearFilters}
        toolbar={
          <LastModifiedFilterBar
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
          />
        }
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} templates`,
        }}
        scroll={{ x: 1400 }}
      />

      <TemplateWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          refetch();
        }}
      />
    </>
  );

  if (embedded) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateNew}
            size="large"
          >
            New Template
          </Button>
        </div>
        {content}
      </div>
    );
  }

  return (
    <LibraryPage
      title="Templates"
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Templates' },
      ]}
      actions={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateNew}
          size="large"
        >
          New Template
        </Button>
      }
    >
      {content}
    </LibraryPage>
  );
}
